import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { NotificationAction, NotificationService } from '../../../core/services/notification.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, TranslocoDirective],
  template: `
    @if (notification(); as notif) {
      <div
        *transloco="let t"
        [@fadeInOut]
        class="fixed bottom-4 right-4 z-[60] max-w-[min(28rem,calc(100vw-2rem))]"
      >
        <div
          class="toast-unifi flex items-center compact-gap px-3 py-2 break-words"
          [ngClass]="edgeClasses[notif.type]"
          role="status"
          aria-live="polite"
        >
          <span>{{ notif.message }}</span>
          @if (notif.action; as action) {
            <button
              class="btn btn-ghost btn-sm px-2 text-inherit font-semibold"
              (click)="runAction(action)"
              [attr.aria-label]="action.label"
            >
              {{ action.label }}
            </button>
          }
          <button
            class="btn btn-ghost btn-sm px-2 text-inherit"
            (click)="close()"
            [attr.aria-label]="t('common.close')"
          >
            ✕
          </button>
        </div>
      </div>
    }
  `,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(8px)' })),
      ]),
    ]),
  ],
})
export class NotificationComponent {
  private notificationService = inject(NotificationService);
  public notification = toSignal(this.notificationService.notification$, {
    initialValue: null,
  });

  public edgeClasses: Record<string, string> = {
    error: 'toast-unifi--error',
    success: 'toast-unifi--success',
    warning: 'toast-unifi--warning',
    info: 'toast-unifi--info',
  };

  close() {
    this.notificationService.hide();
  }

  runAction(action: NotificationAction) {
    action.run();
    this.notificationService.hide();
  }
}
